import React, { useEffect, useState } from "react";
import api, { API } from "../lib/api";
import { Trash2, Link2, QrCode, Upload, Loader2, Check, X, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import SettingsLayout from "./innstillinger/SettingsLayout";

const PLATFORMS = [
  "Snapchat", "Instagram", "TikTok", "X (Twitter)", "YouTube",
  "Patreon", "Ko-fi", "Buy Me a Coffee", "Throne", "Linktree",
  "Beacons", "Discord", "Annet",
];

const STATUS_LABELS = {
  pending: { text: "Venter på godkjenning", color: "text-yellow-400" },
  approved: { text: "Godkjent", color: "text-green-400" },
  rejected: { text: "Avvist", color: "text-red-400" },
};

export default function SelgerLenker() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState(PLATFORMS[0]);
  const [customLabel, setCustomLabel] = useState("");
  const [url, setUrl] = useState("");
  const [qrFile, setQrFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/users/me/seller-links");
      setLinks(r.data.links || []);
    } catch (e) {
      if (e?.response?.status === 403) setAccessDenied(true);
      else toast.error("Kunne ikke laste lenker");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const addLink = async () => {
    const finalLabel = label === "Annet" ? customLabel.trim() : label;
    if (!finalLabel) return toast.error("Velg eller skriv en label");
    if (!url.trim()) return toast.error("URL må fylles ut");
    setSaving(true);
    try {
      const res = await api.post("/users/me/seller-links", { label: finalLabel, url: url.trim() });
      const newLinkId = res?.data?.link?.id;
      // Hvis QR-fil er valgt, last opp umiddelbart
      if (qrFile && newLinkId) {
        try {
          const fd = new FormData();
          fd.append("file", qrFile);
          await api.post(`/users/me/seller-links/${newLinkId}/qr`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (qErr) {
          toast.error("Lenke lagret, men QR-opplasting feilet: " + (qErr?.response?.data?.detail || "ukjent"));
        }
      }
      toast.success("Lenke sendt til godkjenning");
      setLabel(PLATFORMS[0]);
      setCustomLabel("");
      setUrl("");
      setQrFile(null);
      load();
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      const msg = e?.response?.data?.detail?.message || e?.response?.data?.detail || "Kunne ikke legge til";
      if (code === "creator_required") {
        toast.error(msg, { duration: 8000 });
      } else {
        toast.error(typeof msg === "string" ? msg : "Kunne ikke legge til");
      }
    } finally {
      setSaving(false);
    }
  };

  const deleteLink = async (id) => {
    if (!window.confirm("Slette denne lenken?")) return;
    try {
      await api.delete(`/users/me/seller-links/${id}`);
      toast.success("Slettet");
      load();
    } catch {
      toast.error("Kunne ikke slette");
    }
  };

  const uploadQr = async (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/users/me/seller-links/${id}/qr`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("QR-bilde lastet opp");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opplasting feilet");
    }
  };

  if (accessDenied) {
    return (
      <SettingsLayout active="selger-lenker">
        <div className="panel-card p-8 text-center">
          <AlertCircle className="h-10 w-10 text-[var(--magenta)] mx-auto mb-4" />
          <h2 className="font-fraunces italic text-2xl text-[var(--cream)] mb-2">Krever verifisert selger-status</h2>
          <p className="font-manrope text-sm text-[var(--cream-soft)]">
            Du må fullføre selger-verifisering før du kan legge til lenker på profilen din.
          </p>
          <a href="/innstillinger/selger-verifisering" className="inline-block mt-4 px-5 py-2 bg-[var(--magenta)] text-black font-meta text-xs tracking-[0.15em] uppercase">
            Start verifisering
          </a>
        </div>
      </SettingsLayout>
    );
  }

  return (
    <SettingsLayout active="selger-lenker">
      <div className="space-y-8">
        <div>
          <h1 className="font-fraunces italic text-3xl text-[var(--cream)]">Selger-lenker</h1>
          <p className="font-manrope text-sm text-[var(--cream-soft)] mt-2">
            Legg til opptil 2 lenker som vises på din profil — Snapchat, Instagram, Patreon osv. Hver lenke må godkjennes av admin før den vises offentlig.
          </p>
        </div>

        {/* Eksisterende lenker */}
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[var(--cream-soft)]" /></div>
        ) : links.length === 0 ? (
          <p className="text-sm text-[var(--cream-faint)] italic">Ingen lenker lagt til ennå.</p>
        ) : (
          <div className="space-y-3" data-testid="seller-links-list">
            {links.map((link) => {
              const statusInfo = STATUS_LABELS[link.status] || STATUS_LABELS.pending;
              return (
                <div key={link.id} className="panel-card p-4 flex items-start gap-3" data-testid={`seller-link-${link.id}`}>
                  <Link2 className="h-5 w-5 text-[var(--magenta)] shrink-0 mt-1" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[var(--cream)]">{link.label}</div>
                    <a href={link.url} target="_blank" rel="noreferrer" className="text-xs text-[var(--cream-soft)] hover:text-[var(--magenta)] block truncate">
                      {link.url}
                    </a>
                    <div className={`font-meta text-[10px] tracking-[0.15em] uppercase mt-2 ${statusInfo.color}`}>
                      {statusInfo.text}
                      {link.rejection_reason && <span className="block mt-1 text-red-300/70 normal-case tracking-normal">{link.rejection_reason}</span>}
                    </div>
                    {link.qr_image_path ? (
                      <div className="mt-2 flex items-center gap-2 text-xs text-[var(--cream-faint)]">
                        <QrCode className="h-3.5 w-3.5" /> QR-bilde lastet opp
                      </div>
                    ) : (
                      <label className="mt-2 inline-flex items-center gap-2 text-xs text-[var(--cream-soft)] hover:text-[var(--magenta)] cursor-pointer">
                        <Upload className="h-3.5 w-3.5" /> Last opp QR-bilde (valgfri)
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          onChange={(e) => e.target.files?.[0] && uploadQr(link.id, e.target.files[0])}
                        />
                      </label>
                    )}
                  </div>
                  <button onClick={() => deleteLink(link.id)} className="text-[var(--cream-faint)] hover:text-red-400" data-testid={`delete-seller-link-${link.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Legg til ny */}
        {links.length < 2 && (
          <div className="panel-card p-5 space-y-3">
            <h3 className="font-fraunces italic text-lg text-[var(--cream)]">Legg til ny lenke</h3>
            <div>
              <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">PLATTFORM</label>
              <select value={label} onChange={(e) => setLabel(e.target.value)} className="input-editorial w-full" data-testid="seller-link-platform">
                {PLATFORMS.map((p) => <option key={p}>{p}</option>)}
              </select>
            </div>
            {label === "Annet" && (
              <div>
                <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">EGENDEFINERT NAVN</label>
                <input
                  type="text"
                  value={customLabel}
                  onChange={(e) => setCustomLabel(e.target.value)}
                  maxLength={40}
                  className="input-editorial w-full"
                  data-testid="seller-link-custom-label"
                />
              </div>
            )}
            <div>
              <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">URL</label>
              <input
                type="url"
                placeholder="https://..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="input-editorial w-full"
                data-testid="seller-link-url"
              />
            </div>
            <div>
              <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">QR-BILDE (valgfritt)</label>
              <label className="flex items-center gap-3 border border-white/10 px-3 py-2 cursor-pointer hover:border-[var(--magenta)] transition-colors" data-testid="seller-link-qr-label">
                <Upload className="h-3.5 w-3.5 text-[var(--magenta)]" />
                <span className="font-manrope text-sm text-[var(--cream-soft)] flex-1 truncate">
                  {qrFile ? qrFile.name : "Velg QR-kode (JPG/PNG/WebP, maks 5 MB)"}
                </span>
                {qrFile && (
                  <button type="button" onClick={(e) => { e.preventDefault(); setQrFile(null); }} className="text-[var(--cream-faint)] hover:text-[var(--magenta)]" data-testid="seller-link-qr-clear">
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (f.size > 5 * 1024 * 1024) { toast.error("Maks 5 MB"); return; }
                    setQrFile(f);
                  }}
                  data-testid="seller-link-qr-input"
                />
              </label>
              <div className="font-meta text-[10px] text-[var(--cream-faint)] mt-1.5">
                Lastes opp automatisk sammen med lenken. Kan også legges til senere.
              </div>
            </div>
            <button
              onClick={addLink}
              disabled={saving}
              className="px-5 py-2.5 bg-[var(--magenta)] text-black font-meta text-xs tracking-[0.15em] uppercase disabled:opacity-50"
              data-testid="seller-link-submit"
            >
              {saving ? <Loader2 className="h-3 w-3 animate-spin inline mr-2" /> : null}
              Send til godkjenning
            </button>
          </div>
        )}
      </div>
    </SettingsLayout>
  );
}
